import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Link,
  useNavigate,
} from "react-router";
import { useEffect, useState, type MouseEvent } from "react";
import { LogOut } from "lucide-react";
import { ProtectedRoute } from "@/admin/components/ProtectedRoute";
import { ErrorBoundary } from "@/admin/components/ErrorBoundary";
import { EasyOnboardPage } from "@/admin/pages/EasyOnboardPage";
import { ActiveTrialsPage } from "@/admin/pages/ActiveTrialsPage";
import { signOut } from "@/admin/lib/auth-client";
import { TRPCProvider } from "./providers/TRPCProvider";
import nameLogo from "@/assets/branding/name_logo_horizontal.svg";
import { AdminUsers } from "@/admin/pages/AdminUsers";
import { AdminOrgs } from "@/admin/pages/AdminOrgs";
import { AdminPlans } from "@/admin/pages/AdminPlans";
import { AdminPlan } from "@/admin/pages/AdminPlan";
import { AdminSubscriptions } from "@/admin/pages/AdminSubscriptions";
import { AdminUserPage } from "@/admin/pages/AdminUserPage";
import { AdminUserSessionPage } from "@/admin/pages/AdminUserSessionPage";
import { AdminSubscriptionUpgrade } from "@/admin/pages/AdminSubscriptionUpgrade";
import { AdminStripe } from "@/admin/pages/AdminStripe";
import { StripeEventPage } from "@/admin/pages/StripeEventPage";
import { AdminEmailsHome } from "@/admin/pages/AdminEmailsHome";
import { AdminEmailDraft } from "@/admin/pages/AdminEmailDraft";
import { AdminEmailSend } from "@/admin/pages/AdminEmailSend";
import { AdminEmailsList } from "@/admin/pages/AdminEmailsList";
import { AdminEmailJobList } from "@/admin/pages/AdminEmailJobList";
import { AdminEmailJob } from "@/admin/pages/AdminEmailJob";
import { AdminDashboard } from "@/admin/pages/AdminDashboard";
import { Logout } from "@/admin/pages/Logout";
import { AdminRedisSync } from "@/admin/pages/AdminRedisSync";
import { AdminSubscriptionPage } from "@/admin/pages/AdminSubscriptionPage";
import { AdminOrganizationPage } from "@/admin/pages/AdminOrganizationPage";
import { AdminUserCreate } from "@/admin/pages/AdminUserCreate";
import { AdminUserEdit } from "@/admin/pages/AdminUserEdit";
import { AdminPlanCreate } from "@/admin/pages/AdminPlanCreate";
import { AdminOrganizationMemberCreate } from "@/admin/pages/AdminOrganizationMemberCreate";
import {ApiKeyManagementWorkflow, ApiKeyWorkflow, OrganizationWorkflow, ProductWorkflow, SubscriptionWorkflow} from "@/admin/pages/AdminCrudWorkflows";

function BrandFooter() {
  return (
    <footer className="brand-footer" aria-label="NOSIBLE admin footer">
      <div className="brand-footer__base">
        <a href="https://nosible.com">NOSIBLE / ADMIN</a>
        <nav aria-label="NOSIBLE admin footer links">
          <a href="https://nosible.com">NOSIBLE.COM</a>
          <a href="https://app.nosible.com">APP</a>
          <a href="https://nosible.world/world">WORLD</a>
          <a href="https://docs.nosible.com">DOCS</a>
        </nav>
        <span>© NOSIBLE 2026</span>
      </div>
    </footer>
  );
}

function AdminHeader({ onLogout }: { onLogout: () => Promise<void> }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const mobileMedia = window.matchMedia("(max-width: 760px)");
    const previousOverflow = document.body.style.overflow;
    const closeOnDesktop = (event: MediaQueryListEvent) => {
      if (!event.matches) setMobileMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };

    document.body.style.overflow = "hidden";
    mobileMedia.addEventListener("change", closeOnDesktop);
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      mobileMedia.removeEventListener("change", closeOnDesktop);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileMenuOpen]);

  const closeAfterAction = (event: MouseEvent<HTMLDivElement>) => {
    if (
      event.target instanceof Element &&
      event.target.closest("button, a")
    ) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`admin-header ${mobileMenuOpen ? "is-menu-open" : ""}`}
    >
      <div className="admin-header__main">
        <a className="admin-logo" href="/" aria-label="NOSIBLE - administration home">
          <img src={nameLogo} alt="NOSIBLE" />
        </a>

        <div
          id="admin-mobile-menu"
          className={`site-utilities admin-header-actions ${mobileMenuOpen ? "is-open" : ""}`}
          aria-label="Admin menu"
          onClick={closeAfterAction}
        >
          <Link className="easy-onboard-cta" to="/easy-onboard">
            LAUNCH TRIAL
          </Link>
          <Link className="active-trials-cta" to="/active-trials">
            ACTIVE TRIALS
          </Link>
          <button
            className="site-utility-link site-utility-link--button"
            type="button"
            onClick={() => void onLogout()}
          >
            <LogOut aria-hidden="true" />
            SIGN OUT
          </button>
        </div>

        <button
          type="button"
          className={`mobile-menu-button ${mobileMenuOpen ? "open" : ""}`}
          aria-label={mobileMenuOpen ? "Close admin menu" : "Open admin menu"}
          aria-expanded={mobileMenuOpen}
          aria-controls="admin-mobile-menu"
          onClick={() => setMobileMenuOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </header>
  );
}

function AdminLayout() {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut({
      fetchOptions: {
        onSuccess: () => navigate("/login"),
      },
    });
  };

  return (
    <ProtectedRoute requireAdmin={true}>
      <div className="admin-shell">
        <a className="skip-link" href="#admin-main">SKIP TO CONTENT</a>
        <AdminHeader onLogout={handleLogout} />
        <main id="admin-main" className="admin-route"><Outlet /></main>
        <BrandFooter />
      </div>
    </ProtectedRoute>
  );
}

const router = createBrowserRouter([
  {
    path: "/logout",
    Component: Logout,
    ErrorBoundary,
  },
  {
    path: "/",
    Component: AdminLayout,
    ErrorBoundary,
    children: [
      { index: true, Component: AdminDashboard },
      { path: "easy-onboard", Component: EasyOnboardPage },
      { path: "active-trials", Component: ActiveTrialsPage },
      { path: "organizations/create", Component: () => <OrganizationWorkflow action="create" /> },
      { path: "organizations/update", Component: () => <OrganizationWorkflow action="update" /> },
      { path: "organizations/delete", Component: () => <OrganizationWorkflow action="delete" /> },
      { path: "products/create", Component: () => <ProductWorkflow action="create" /> },
      { path: "products/update", Component: () => <ProductWorkflow action="update" /> },
      { path: "products/delete", Component: () => <ProductWorkflow action="delete" /> },
      { path: "products/deactivate", Component: () => <ProductWorkflow action="deactivate" /> },
      { path: "api-keys/create", Component: () => <ApiKeyWorkflow action="create" /> },
      { path: "api-keys/update", Component: () => <ApiKeyManagementWorkflow action="update" /> },
      { path: "api-keys/delete", Component: () => <ApiKeyWorkflow action="delete" /> },
      { path: "api-keys/review", Component: () => <ApiKeyManagementWorkflow action="review" /> },
      { path: "api-keys/deactivate", Component: () => <ApiKeyManagementWorkflow action="deactivate" /> },
      { path: "subscriptions/update", Component: () => <SubscriptionWorkflow action="update" /> },
      { path: "subscriptions/delete", Component: () => <SubscriptionWorkflow action="delete" /> },
      { path: "subscriptions/cancel", Component: () => <SubscriptionWorkflow action="cancel" /> },
      { path: "rate-limits/products", Component: () => <ProductWorkflow action="update" /> },
      { path: "rate-limits/subscriptions", Component: () => <SubscriptionWorkflow action="update" /> },
      { path: "users", Component: AdminUsers },
      { path: "users/create", Component: AdminUserCreate },
      { path: "users/:userId/edit", Component: AdminUserEdit },
      { path: "users/:userId", Component: AdminUserPage },
      { path: "users/:userId/sessions", Component: AdminUserSessionPage },
      { path: "organizations", Component: AdminOrgs },
      { path: "organizations/add-member", Component: AdminOrganizationMemberCreate },
      { path: "organizations/:slug", Component: AdminOrganizationPage },
      { path: "plans", Component: AdminPlans },
      { path: "plans/create", Component: AdminPlanCreate },
      { path: "plans/:planId", Component: AdminPlan },
      { path: "subscriptions", Component: AdminSubscriptions },
      { path: "subscriptions/:subscriptionId", Component: AdminSubscriptionPage },
      { path: "subscriptions/:subscriptionId/adminUpgrade", Component: AdminSubscriptionUpgrade },
      { path: "subscriptions/create", Component: () => <SubscriptionWorkflow action="create" /> },
      { path: "stripe", Component: AdminStripe },
      { path: "stripe/events/:eventId", Component: StripeEventPage },
      { path: "emails", Component: AdminEmailsHome },
      { path: "emails/custom", Component: AdminEmailDraft },
      { path: "emails/custom/send", Component: AdminEmailSend },
      { path: "emails/list", Component: AdminEmailsList },
      { path: "emails/jobs", Component: AdminEmailJobList },
      { path: "emails/jobs/:jobId", Component: AdminEmailJob },
      { path: "redis-sync", Component: AdminRedisSync },
    ],
  },
]);

export function AdminApp() {
  return (
    <TRPCProvider>
      <RouterProvider router={router} />
    </TRPCProvider>
  );
}
